/**
 * Reports API client (Inventory with Min & Max, Stockout Aging) — talks to
 * mttr/app_api. Available to every authenticated role; scoped server-side to
 * the caller's visible SCUs. `workcenter` limits to one location.
 */

import { ApiError, apiFetch } from "./apiClient";

export interface MinMaxLine {
  scu_name: string;
  smartbox_code: string | null;
  workcenter_name: string | null;
  item_id: number;
  sku: string | null;
  name: string | null;
  on_hand: number;
  min_qty: number;
  suggest_qty: number | null;
  /** False when the box's latest scan read 0 tags — on_hand=0 means "no data",
   *  not genuinely zero stock. */
  has_scan_data: boolean;
}

export interface StockoutLine {
  scu_name: string;
  smartbox_code: string | null;
  workcenter_name: string | null;
  item_id: number;
  sku: string | null;
  name: string | null;
  min_qty: number;
  suggest_qty: number | null;
  stockout_since_utc: string | null;
}

export interface MinMaxReport {
  total: number;
  lines: MinMaxLine[];
}

export interface StockoutReport {
  total: number;
  lines: StockoutLine[];
}

export class ReportApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ReportApiError";
  }
}

async function getJson<T>(path: string, token: string): Promise<T> {
  try {
    return await apiFetch<T>(path, token);
  } catch (e) {
    throw e instanceof ApiError ? new ReportApiError(e.message, e.status) : e;
  }
}

/** Inventory with Min & Max — every min-assigned item at a location with its
 *  current on-hand. A specific `workcenter` is required (the fleet-wide set is
 *  tens of thousands of rows). */
export function fetchInventoryMinmax(
  token: string,
  workcenter: string,
): Promise<MinMaxReport> {
  return getJson<MinMaxReport>(
    `/api/reports/inventory-minmax?workcenter=${encodeURIComponent(workcenter)}`,
    token,
  );
}

/** Stockout Aging — min-assigned items now at zero on-hand, with the scan they
 *  were last seen in (the stockout start). `workcenter` omitted = all of the
 *  caller's boxes. */
export function fetchStockoutAging(
  token: string,
  workcenter?: string,
): Promise<StockoutReport> {
  const q = workcenter ? `?workcenter=${encodeURIComponent(workcenter)}` : "";
  return getJson<StockoutReport>(`/api/reports/stockout-aging${q}`, token);
}

// ----- Inventory Optimizer ------------------------------------------------

export type OptimizerGranularity = "fleet" | "workcenter" | "smartbox";

export interface OptimizerLine {
  item_id: number;
  sku: string | null;
  name: string | null;
  scu_name: string | null;
  smartbox_code: string | null;
  workcenter_name: string | null;
  lead_time_days: number;
  is_critical: boolean;
  unit_price: number | null;
  qty_consumed: number;
  avg_daily_consumption: number;
  on_hand: number;
  days_on_hand: number | null;
  min_qty: number | null;
  suggest_qty: number | null;
  target_days_min: number;
  target_days_max: number;
  recommended_min: number;
  recommended_max: number;
  refill_qty: number;
  excess_qty: number;
  value_of_excess: number | null;
  replenishment_cost: number | null;
  update_recommended: boolean;
}

export interface OptimizerReport {
  total: number;
  since: string;       // ISO date
  until: string;
  granularity: OptimizerGranularity;
  workcenter: string | null;
  /** Where consumption came from; always "confirmed_departures" now. */
  source: string;
  lines: OptimizerLine[];
}

export function fetchInventoryOptimizer(
  token: string,
  opts: {
    granularity?: OptimizerGranularity;
    workcenter?: string | null;
    since?: string;
    until?: string;
  } = {},
): Promise<OptimizerReport> {
  const p = new URLSearchParams();
  if (opts.granularity) p.set("granularity", opts.granularity);
  if (opts.workcenter)  p.set("workcenter", opts.workcenter);
  if (opts.since)       p.set("since", opts.since);
  if (opts.until)       p.set("until", opts.until);
  const qs = p.toString();
  return getJson<OptimizerReport>(
    `/api/reports/inventory-optimizer${qs ? `?${qs}` : ""}`,
    token,
  );
}
